import {
  REHAB_FORM_NAMES,
  REHAB_FORM_SUB_TYPES,
  REHAB_FORM_TYPES,
} from "core/consts";
import { getGKVGeneralFormSchema } from "core/schemas/GKVGeneralFormSchema";
import { getGKVMedicalFormSchema } from "core/schemas/GKVMedicalFormSchema";
import { flattenZodFieldErrorsDeep, unflattenErrors } from "core/schemas/utils";
import {
  Auction,
  GetOntologyType,
  GKVGeneralFormData,
  GKVMedicalFormData,
  RehabFormName,
  RehabFormSubType,
  RehabFormType,
  ValueOf,
} from "core/types";
import { PDFForm } from "pdf-lib";
import { ConversionJobs, ValidationFunction } from "react-forms-state";
import { ObservableValue } from "react-forms-state/src/Context";
import { Observable } from "rxjs";
import Translations from "translations/types";
import { DRVGeneralFormPresenter } from "./DRVGeneralForm/FormSections";
import { importAuctionToDRVGeneralForm } from "./DRVGeneralForm/importAuction";
import { DRVgeneralFormDefinition } from "./DRVGeneralForm/modelDefinition";
import { DRVGeneralFormMapping } from "./DRVGeneralForm/pdfMapping";
import { DRVMedicalFormPresenter } from "./DRVMedicalForm/FormSections";
import { importAuctionToDRVMedicalForm } from "./DRVMedicalForm/importAuction";
import { DRVmedicalFormDefinition } from "./DRVMedicalForm/modelDefinition";
import { DRVMedicalFormMapping } from "./DRVMedicalForm/pdfMapping";
import { GKVGeneralFormPresenter } from "./GKVGeneralForm";
import { importAuctionToGKVGeneralForm } from "./GKVGeneralForm/importAuction";
import { GKVgeneralFormDefinition } from "./GKVGeneralForm/modelDefinition";
import { GKVMedicalFormPresenter } from "./GKVMedicalForm";
import { importAuctionToGKVMedicalForm } from "./GKVMedicalForm/importAuction";
import { GKVmedicalFormDefinition } from "./GKVMedicalForm/modelDefinition";
import { RehabFormDataMapped } from "./utils";

export const GKV_GENERAL_FORM_PATH =
  "rehab_general_universal_third_draft.pdf" as const;
export const GKV_MEDICAL_FORM_PATH =
  "rehab_medical_universal_fifth_draft.pdf" as const;
export const DRV_GENERAL_FORM_PATH = "general_pension_form_v3.pdf" as const;
export const DRV_MEDICAL_FORM_PATH = "medical_pension_form_v1.1.pdf" as const;

export const GKV_GENERAL_FILE_NAME =
  "antrag_anschlussrehabilitation.pdf" as const;
export const GKV_MEDICAL_FILE_NAME =
  "antrag_aerztlicher_befundbericht.pdf" as const;
export const DRV_GENERAL_FORM_NAME = "drv_antrag_G0250.pdf" as const;
export const DRV_MEDICAL_FORM_NAME = "drv_G0260_G0600.pdf" as const;

const REHAB_FORM_PATHS = [
  GKV_GENERAL_FORM_PATH,
  GKV_MEDICAL_FORM_PATH,
  DRV_GENERAL_FORM_PATH,
  DRV_MEDICAL_FORM_PATH,
] as const;

export type RehabFormPath = (typeof REHAB_FORM_PATHS)[number];

const REHAB_FORM_FILE_NAMES = [
  GKV_GENERAL_FILE_NAME,
  GKV_MEDICAL_FILE_NAME,
  DRV_GENERAL_FORM_NAME,
  DRV_MEDICAL_FORM_NAME,
] as const;

export type RehabFormFileName = (typeof REHAB_FORM_FILE_NAMES)[number];

export type RehabFormFormGetters<T extends RehabFormName> = Partial<
  Record<
    keyof RehabFormDataMapped<T>,
    (auction: Auction) => ValueOf<RehabFormDataMapped<T>> | null
  >
>;

export type PdfFillerProps<T extends RehabFormName> = {
  form: PDFForm;
  formData: RehabFormDataMapped<T>;
  locale: Locale;
};

export type PdfFiller<T extends RehabFormName> = ({
  form,
  formData,
  locale,
}: PdfFillerProps<T>) => void;

type FormConfig = {
  Form: (props: {
    onChange: (value: any, statePath: string, validation?: any) => void;
    skipRequiredValidation: boolean;
    valueChangeObs: Observable<ObservableValue> | undefined;
  }) => JSX.Element;
  convertOut?: (value: any, props: any) => AnyObject;
  getFormDescription: (translations: Translations) => string;
  getFormTitle: (translations: Translations) => string;
  importAuctionToFormGetters: (
    getOntology: GetOntologyType,
    translations: Translations,
  ) => RehabFormFormGetters<any>;
  modelDefinition: ConversionJobs;
  validate?: ValidationFunction;
};

type PdfConfig = {
  blankPdfAssetPath: RehabFormPath;
  downloadPdfFileName: RehabFormFileName;
  pdfFiller?: PdfFiller<any>;
};

type RehabFormConfig = {
  formConfig: FormConfig;
  formSubType: RehabFormSubType;
  formType: RehabFormType;
  pdfConfig: PdfConfig;
};

export const REHAB_FORM_CONFIGS: Record<RehabFormName, RehabFormConfig> = {
  [REHAB_FORM_NAMES.GKV_GENERAL]: {
    formType: REHAB_FORM_TYPES.GENERAL,
    formSubType: REHAB_FORM_SUB_TYPES.GKV,
    formConfig: {
      Form: GKVGeneralFormPresenter,
      getFormTitle: (translations: Translations) =>
        translations.patientForms.generalForm.title,
      getFormDescription: (translations: Translations) =>
        translations.patientForms.generalForm.description,
      modelDefinition: GKVgeneralFormDefinition,
      validate(
        value: GKVGeneralFormData,
        props: {
          skipValidation?: boolean;
          translations: Translations;
        },
      ) {
        const { skipValidation, translations } = props;

        if (skipValidation) {
          return true;
        }

        const result = getGKVGeneralFormSchema({ translations }).safeParse(
          value,
        );
        if (!result.success) {
          const flattened = flattenZodFieldErrorsDeep(result.error);
          const nested = unflattenErrors(flattened);
          return nested;
        }
        return true;
      },
      importAuctionToFormGetters: importAuctionToGKVGeneralForm,
    },
    pdfConfig: {
      blankPdfAssetPath: GKV_GENERAL_FORM_PATH,
      downloadPdfFileName: GKV_GENERAL_FILE_NAME,
      // pdfFiller: GKVGeneralFormMapping, #TODO will have to be re-implemented later
    },
  },
  [REHAB_FORM_NAMES.GKV_MEDICAL]: {
    formType: REHAB_FORM_TYPES.MEDICAL,
    formSubType: REHAB_FORM_SUB_TYPES.GKV,
    formConfig: {
      Form: GKVMedicalFormPresenter,
      getFormTitle: (translations: Translations) =>
        translations.patientForms.medicalForm.title,
      getFormDescription: (translations: Translations) =>
        translations.patientForms.medicalForm.description,
      modelDefinition: GKVmedicalFormDefinition,
      validate(
        value: GKVMedicalFormData,
        props: {
          skipRequiredValidation?: boolean;
          skipValidation?: boolean;
          translations: Translations;
        },
      ) {
        const { skipRequiredValidation, skipValidation, translations } = props;

        if (skipValidation) {
          return true;
        }

        const result = getGKVMedicalFormSchema({
          translations,
          skipRequiredValidation,
        }).safeParse(value);

        if (!result.success) {
          const flattened = flattenZodFieldErrorsDeep(result.error);
          const nested = unflattenErrors(flattened);
          return nested;
        }
        return true;
      },
      importAuctionToFormGetters: importAuctionToGKVMedicalForm,
    },
    pdfConfig: {
      blankPdfAssetPath: GKV_MEDICAL_FORM_PATH,
      downloadPdfFileName: GKV_MEDICAL_FILE_NAME,
      // pdfFiller: GKVmedicalFormMapping, #TODO will have to be re-implemented later
    },
  },
  [REHAB_FORM_NAMES.DRV_GENERAL]: {
    formType: REHAB_FORM_TYPES.GENERAL,
    formSubType: REHAB_FORM_SUB_TYPES.DRV,
    formConfig: {
      Form: DRVGeneralFormPresenter,
      getFormTitle: (translations: Translations) =>
        translations.patientForms.drvGeneralForm.title,
      getFormDescription: (translations: Translations) =>
        translations.patientForms.drvGeneralForm.description,
      modelDefinition: DRVgeneralFormDefinition,
      importAuctionToFormGetters: importAuctionToDRVGeneralForm,
    },
    pdfConfig: {
      blankPdfAssetPath: DRV_GENERAL_FORM_PATH,
      downloadPdfFileName: DRV_GENERAL_FORM_NAME,
      pdfFiller: DRVGeneralFormMapping,
    },
  },
  [REHAB_FORM_NAMES.DRV_MEDICAL]: {
    formType: REHAB_FORM_TYPES.MEDICAL,
    formSubType: REHAB_FORM_SUB_TYPES.DRV,
    formConfig: {
      Form: DRVMedicalFormPresenter,
      getFormTitle: (translations: Translations) =>
        translations.patientForms.drvMedicalForm.title,
      getFormDescription: (translations: Translations) =>
        translations.patientForms.drvMedicalForm.description,
      modelDefinition: DRVmedicalFormDefinition,
      importAuctionToFormGetters: importAuctionToDRVMedicalForm,
    },
    pdfConfig: {
      blankPdfAssetPath: DRV_MEDICAL_FORM_PATH,
      downloadPdfFileName: DRV_MEDICAL_FORM_NAME,
      pdfFiller: DRVMedicalFormMapping,
    },
  },
};

export const getRehabFormConfig = (formName: RehabFormName): RehabFormConfig =>
  REHAB_FORM_CONFIGS[formName];
